import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import FileUpload, {
  type UploadedFileMeta,
} from 'shared/components/upload/FileUpload';
import type { AIProcessingPhase } from 'shared/components/AIDocumentScanLoader';
import { useCourierOnboarding } from 'features/courier-onboarding/context/CourierOnboardingContext';
import { extractDocument } from 'features/courier-onboarding/services/extractDocument';
import { validateProfilePhoto } from 'features/courier-onboarding/services/validateProfilePhoto';
import type {
  CourierBankAccountInfo,
  CourierDocumentKey,
  CourierPersonalInfo,
  ProfilePhotoReason,
} from 'features/courier-onboarding/types';
import ProfilePhotoGuide from 'features/courier-onboarding/components/upload/ProfilePhotoGuide';
import RemoveDocumentModal, {
  type DependentField,
} from 'features/courier-onboarding/components/upload/RemoveDocumentModal';
import {
  normalizeBirthDate,
  normalizeIdentityNumber,
  shouldReextractDriverLicense,
} from 'features/courier-onboarding/utils/documentExtraction';

/** Document slots that trigger an OCR extraction on upload. */
const OCR_DOC_KEYS: CourierDocumentKey[] = [
  'driverLicense',
  'bankAccount',
  'businessLicense',
];

const IMAGE_ONLY_ACCEPT: Record<string, string[]> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
};

const DOC_LABEL_KEY: Record<CourierDocumentKey, string> = {
  profilePicture: 'onboarding.documents.profilePicture',
  driverLicense: 'onboarding.documents.driverLicense',
  bankAccount: 'onboarding.documents.bankAccount',
  businessLicense: 'onboarding.documents.businessLicense',
};

type ExtractionStatus = 'idle' | 'extracting' | 'success' | 'failed';

export interface SlotExtraction {
  key: CourierDocumentKey;
  /** Digits-only 9-digit ID extracted from this document, if any. */
  extractedIdNumber?: string;
  /** Normalized YYYY-MM-DD birth date (driver license only). */
  birthDate?: string | null;
}

interface Props {
  docKey: CourierDocumentKey;
  required?: boolean;
  /** Fires whenever OCR resolves (or is cleared) so the parent can gate. */
  onExtraction?: (info: SlotExtraction | null) => void;
  /** Reports upload/extraction activity so the parent can gate the Next button. */
  onBusyChange?: (key: CourierDocumentKey, busy: boolean) => void;
}

const CourierDocumentSlot: React.FC<Props> = ({
  docKey,
  required,
  onExtraction,
  onBusyChange,
}) => {
  const { t } = useTranslation();
  const {
    data,
    setPersonal,
    setBankAccount,
    upsertDocument,
    removeDocument,
    setDocumentOcrStatus,
    setOcrExtraction,
  } = useCourierOnboarding();

  const ocr = OCR_DOC_KEYS.includes(docKey);
  const isProfile = docKey === 'profilePicture';
  const checked = ocr || isProfile;
  const label = t(DOC_LABEL_KEY[docKey]) as string;

  const existing = data.documents.find((d) => d.key === docKey);
  const value: UploadedFileMeta | undefined = existing
    ? {
        name: existing.name,
        type: existing.type,
        size: existing.size,
        url: existing.url ?? '',
      }
    : undefined;

  const [status, setStatus] = useState<ExtractionStatus>(() => {
    if (!existing) return 'idle';
    if (docKey === 'profilePicture') {
      if (existing.profileCheckStatus === 'accepted') return 'success';
      if (existing.profileCheckStatus === 'rejected') return 'failed';
      return 'idle';
    }
    return existing.ocrStatus ?? 'success';
  });
  const [isUploading, setIsUploading] = useState(false);
  const [processingPhase, setProcessingPhase] =
    useState<AIProcessingPhase | null>(null);

  const [removeOpen, setRemoveOpen] = useState(false);

  const previousUrlRef = useRef<string | undefined>(existing?.url ?? undefined);
  const restoredExtractionStartedRef = useRef(false);
  const runDriverLicenseOcrRef = useRef<
    ((url: string, overwrite: boolean) => Promise<void>) | null
  >(null);
  const profileCheckStartedRef = useRef(false);
  const runProfileCheckRef = useRef<
    ((meta: UploadedFileMeta) => Promise<void>) | null
  >(null);

  /** Surface the extraction both to the parent and to shared state so the
   * documents-step guards (age, cross-document ID mismatch) survive navigating
   * back to this step from Review. */
  const applyExtraction = (info: SlotExtraction | null) => {
    setOcrExtraction(
      docKey,
      info
        ? { extractedIdNumber: info.extractedIdNumber, birthDate: info.birthDate }
        : null,
    );
    onExtraction?.(info);
  };

  const finishExtraction = (nextStatus: 'success' | 'failed') => {
    setStatus(nextStatus);
    setDocumentOcrStatus(docKey, nextStatus);
  };

  useEffect(() => {
    if (checked && isUploading) setProcessingPhase('uploading');
  }, [checked, isUploading]);

  useEffect(() => {
    onBusyChange?.(docKey, isUploading || status === 'extracting');
  }, [docKey, isUploading, status, onBusyChange]);

  useEffect(() => {
    if (!checked) return;
    if (status === 'extracting') {
      setProcessingPhase('extracting');
    } else if (status === 'success' || status === 'failed') {
      setProcessingPhase((prev) => (prev ? 'complete' : null));
    }
  }, [checked, status]);

  const runDriverLicenseOcr = async (url: string, overwrite: boolean) => {
    const result = await extractDocument({ docType: 'driverLicense', url });
    if (!result.ok) {
      finishExtraction('failed');
      applyExtraction(null);
      return;
    }
    const patch: Partial<CourierPersonalInfo> = {};
    const id =
      normalizeIdentityNumber(result.fields.ownerId) ??
      normalizeIdentityNumber(result.extractedIdNumber);
    // The driver license only drives the Tax ID for the finance-company track,
    // where it is the sole ID source. For the licensed-business track the Tax ID
    // comes from the business license / bank documents and the license ID is
    // allowed to differ, so it must not overwrite the Tax ID here.
    const track = data.metadata.businessTrack ?? 'LICENSED_BUSINESS';
    if (
      id &&
      track === 'FINANCIAL_COMPANY' &&
      (overwrite || !data.personalInfo.taxId)
    )
      patch.taxId = id;
    const dob = normalizeBirthDate(result.fields.birthDate);
    if (dob && (overwrite || !data.personalInfo.dateOfBirth))
      patch.dateOfBirth = dob;
    if (Object.keys(patch).length > 0) setPersonal(patch);
    const hasExtractedData = Boolean(
      id || dob,
    );
    if (!hasExtractedData) {
      finishExtraction('failed');
      applyExtraction(null);
      return;
    }
    finishExtraction('success');
    applyExtraction({
      key: docKey,
      extractedIdNumber: id,
      birthDate: dob,
    });
  };

  const runBankAccountOcr = async (url: string, overwrite: boolean) => {
    const result = await extractDocument({ docType: 'bankConfirmation', url });
    if (!result.ok) {
      finishExtraction('failed');
      applyExtraction(null);
      return;
    }
    const surfaced: SlotExtraction = {
      key: docKey,
      extractedIdNumber: result.extractedIdNumber,
    };
    if (!result.bank) {
      if (result.extractedIdNumber) {
        finishExtraction('success');
        applyExtraction(surfaced);
      } else {
        finishExtraction('failed');
        applyExtraction(null);
      }
      return;
    }

    const fills: Partial<CourierBankAccountInfo> = {};
    (
      [
        'bankCode',
        'bankName',
        'bankBranchNumber',
        'bankAccountNumber',
        'bankAccountOwnerId',
      ] as Array<keyof CourierBankAccountInfo>
    ).forEach((field) => {
      const newValue = result.bank?.[field];
      if (!newValue) return;
      if (overwrite || !data.bankAccountInfo[field]) fills[field] = newValue;
    });

    if (Object.keys(fills).length > 0) setBankAccount(fills);
    finishExtraction('success');
    applyExtraction(surfaced);
  };

  const runBusinessLicenseOcr = async (url: string, overwrite: boolean) => {
    const result = await extractDocument({ docType: 'osekCertificate', url });
    if (!result.ok) {
      finishExtraction('failed');
      applyExtraction(null);
      return;
    }
    const fills: Partial<CourierPersonalInfo> = {};

    const taxIdDigits = result.fields.taxId?.replace(/\D/g, '');
    const nextTaxId =
      taxIdDigits && taxIdDigits.length === 9 ? taxIdDigits : undefined;
    const nextName = result.fields.ownerName?.trim() || undefined;

    (
      [
        { field: 'taxId', value: nextTaxId },
        { field: 'fullName', value: nextName },
      ] as Array<{ field: 'taxId' | 'fullName'; value: string | undefined }>
    ).forEach(({ field, value: newValue }) => {
      if (!newValue) return;
      if (overwrite || !data.personalInfo[field])
        (fills as Record<string, string>)[field] = newValue;
    });

    const osekOwnerName = result.fields.ownerName?.trim();
    const officialBusinessName =
      result.fields.officialBusinessName?.trim() || osekOwnerName;
    const bankPatch: Partial<CourierBankAccountInfo> = {};
    if (
      osekOwnerName &&
      (overwrite || !data.bankAccountInfo.bankAccountOwnerName)
    )
      bankPatch.bankAccountOwnerName = osekOwnerName;
    if (
      officialBusinessName &&
      (overwrite || !data.bankAccountInfo.businessName)
    )
      bankPatch.businessName = officialBusinessName;
    if (Object.keys(bankPatch).length > 0) setBankAccount(bankPatch);

    if (Object.keys(fills).length > 0) setPersonal(fills);
    const hasExtractedData = Boolean(
      nextTaxId || nextName || result.extractedIdNumber,
    );
    if (!hasExtractedData) {
      finishExtraction('failed');
      applyExtraction(null);
      return;
    }
    finishExtraction('success');
    applyExtraction({
      key: docKey,
      extractedIdNumber: result.extractedIdNumber ?? nextTaxId,
    });
  };

  const runProfileCheck = async (meta: UploadedFileMeta) => {
    const result = await validateProfilePhoto(meta.url);
    if (previousUrlRef.current !== meta.url) return;
    if (!result.accepted || !result.url) {
      upsertDocument({
        key: 'profilePicture',
        name: meta.name,
        type: meta.type,
        size: meta.size,
        url: meta.url,
        profileCheckStatus: 'rejected',
        profileRejectionReasons: result.reasons,
      });
      setStatus('failed');
      return;
    }
    upsertDocument({
      key: 'profilePicture',
      name: meta.name,
      type: 'image/webp',
      size: meta.size,
      url: result.url,
      profileCheckStatus: 'accepted',
    });
    setStatus('success');
  };

  runDriverLicenseOcrRef.current = runDriverLicenseOcr;
  runProfileCheckRef.current = runProfileCheck;

  const persistedExtraction = data.ocrExtractions[docKey];
  useEffect(() => {
    const url = existing?.url;
    if (
      docKey !== 'driverLicense' ||
      !url ||
      restoredExtractionStartedRef.current ||
      !shouldReextractDriverLicense(url, persistedExtraction)
    )
      return;

    restoredExtractionStartedRef.current = true;
    setStatus('extracting');
    void runDriverLicenseOcrRef.current?.(url, false);
  }, [docKey, existing?.url, persistedExtraction]);

  useEffect(() => {
    if (docKey !== 'profilePicture') return;
    if (!existing?.url || existing.profileCheckStatus) return;
    if (profileCheckStartedRef.current) return;
    profileCheckStartedRef.current = true;
    setStatus('extracting');
    void runProfileCheckRef.current?.({
      name: existing.name,
      type: existing.type,
      size: existing.size,
      url: existing.url,
    });
  }, [
    docKey,
    existing?.url,
    existing?.profileCheckStatus,
    existing?.name,
    existing?.type,
    existing?.size,
  ]);

  const clearMondayOnlyFields = () => {
    if (docKey === 'businessLicense') setBankAccount({ businessName: '' });
    if (docKey === 'bankAccount') setBankAccount({ bankCode: '' });
  };

  const handleChange = (meta: UploadedFileMeta | null) => {
    if (!meta) {
      removeDocument(docKey);
      clearMondayOnlyFields();
      previousUrlRef.current = undefined;
      setStatus('idle');
      applyExtraction(null);
      return;
    }
    upsertDocument({
      key: docKey,
      name: meta.name,
      type: meta.type,
      size: meta.size,
      url: meta.url,
    });
  };

  /**
   * Reset the fields this document prefills back to empty. Used before a
   * re-upload so a replaced (or wrong) file can never leave stale OCR values
   * behind - if the fresh extraction fails or returns nothing, the fields stay
   * empty and downstream validation blocks the submission.
   */
  const clearDependentFields = () => {
    const personalPatch: Record<string, string> = {};
    const bankPatch: Record<string, string> = {};
    buildDependents().forEach((f) => {
      if (f.section === 'personalInfo') personalPatch[f.field] = '';
      else if (f.section === 'bankAccountInfo') bankPatch[f.field] = '';
    });
    if (Object.keys(personalPatch).length > 0)
      setPersonal(personalPatch as Partial<CourierPersonalInfo>);
    if (Object.keys(bankPatch).length > 0)
      setBankAccount(bankPatch as Partial<CourierBankAccountInfo>);
    clearMondayOnlyFields();
  };

  const handleUploaded = (meta: UploadedFileMeta) => {
    const isReplacement = Boolean(
      previousUrlRef.current && previousUrlRef.current !== meta.url,
    );
    previousUrlRef.current = meta.url;
    if (docKey === 'profilePicture') {
      profileCheckStartedRef.current = true;
      setStatus('extracting');
      void runProfileCheck(meta);
      return;
    }
    if (!ocr) {
      setStatus('success');
      return;
    }
    // On replacement, wipe the previously extracted values first so the new
    // file's OCR fully drives the fields (overwrite), and a wrong file leaves
    // them empty.
    if (isReplacement) {
      clearDependentFields();
      applyExtraction(null);
    }
    // The upload callback owns this extraction; prevent the draft-hydration
    // effect from starting a duplicate request after the document state renders.
    restoredExtractionStartedRef.current = true;
    setStatus('extracting');
    if (docKey === 'driverLicense')
      void runDriverLicenseOcr(meta.url, isReplacement);
    else if (docKey === 'bankAccount')
      void runBankAccountOcr(meta.url, isReplacement);
    else if (docKey === 'businessLicense')
      void runBusinessLicenseOcr(meta.url, isReplacement);
  };

  const buildDependents = (): DependentField[] => {
    if (docKey === 'driverLicense') {
      const track = data.metadata.businessTrack ?? 'LICENSED_BUSINESS';
      const deps: DependentField[] = [
        {
          section: 'personalInfo',
          field: 'dateOfBirth',
          label: t('onboarding.personal.dateOfBirth') as string,
          currentValue: data.personalInfo.dateOfBirth,
        },
      ];
      // The license only owns the Tax ID field in the finance-company track.
      if (track === 'FINANCIAL_COMPANY') {
        deps.unshift({
          section: 'personalInfo',
          field: 'taxId',
          label: t('onboarding.personal.taxId') as string,
          currentValue: data.personalInfo.taxId,
        });
      }
      return deps;
    }
    if (docKey === 'businessLicense') {
      return [
        {
          section: 'personalInfo',
          field: 'taxId',
          label: t('onboarding.personal.taxId') as string,
          currentValue: data.personalInfo.taxId,
        },
        {
          section: 'personalInfo',
          field: 'fullName',
          label: t('onboarding.personal.fullName') as string,
          currentValue: data.personalInfo.fullName,
        },
        {
          section: 'bankAccountInfo',
          field: 'bankAccountOwnerName',
          label: t('onboarding.bankAccount.bankAccountOwnerName') as string,
          currentValue: data.bankAccountInfo.bankAccountOwnerName,
        },
      ];
    }
    if (docKey === 'bankAccount') {
      return (
        [
          ['bankName', 'onboarding.bankAccount.bankName'],
          ['bankBranchNumber', 'onboarding.bankAccount.bankBranchNumber'],
          ['bankAccountNumber', 'onboarding.bankAccount.bankAccountNumber'],
          ['bankAccountOwnerId', 'onboarding.bankAccount.bankAccountOwnerId'],
        ] as Array<[keyof CourierBankAccountInfo, string]>
      ).map(([field, labelKey]) => ({
        section: 'bankAccountInfo',
        field: field as string,
        label: t(labelKey) as string,
        currentValue: data.bankAccountInfo[field],
      }));
    }
    return [];
  };

  const handleRemoveConfirm = (resetFields: DependentField[]) => {
    removeDocument(docKey);
    clearMondayOnlyFields();
    previousUrlRef.current = undefined;
    setStatus('idle');

    const personalPatch: Record<string, string> = {};
    const bankPatch: Record<string, string> = {};
    resetFields.forEach((f) => {
      if (f.section === 'personalInfo') personalPatch[f.field] = '';
      else if (f.section === 'bankAccountInfo') bankPatch[f.field] = '';
    });
    if (Object.keys(personalPatch).length > 0)
      setPersonal(personalPatch as Partial<CourierPersonalInfo>);
    if (Object.keys(bankPatch).length > 0)
      setBankAccount(bankPatch as Partial<CourierBankAccountInfo>);

    setRemoveOpen(false);
    applyExtraction(null);
  };

  const rejectionReasons: ProfilePhotoReason[] =
    existing?.profileRejectionReasons && existing.profileRejectionReasons.length > 0
      ? existing.profileRejectionReasons
      : ['check_unavailable'];

  return (
    <section
      data-field={docKey}
      className="flex scroll-mt-24 flex-col gap-3 py-5 first:pt-0 last:pb-0"
    >
      {isProfile ? <ProfilePhotoGuide /> : null}
      <FileUpload
        label={label}
        required={required}
        value={value}
        onChange={handleChange}
        onUploaded={handleUploaded}
        onRequestRemove={ocr ? () => setRemoveOpen(true) : undefined}
        onUploadingChange={checked ? setIsUploading : undefined}
        hideInlineLoader={checked}
        processingPhase={checked ? processingPhase : undefined}
        accept={
          docKey === 'driverLicense' || docKey === 'profilePicture'
            ? IMAGE_ONLY_ACCEPT
            : undefined
        }
        acceptedFormatsLabel={
          docKey === 'driverLicense' || docKey === 'profilePicture'
            ? (t('documents.acceptedImageFormats') as string)
            : undefined
        }
        onProcessingDone={() => setProcessingPhase(null)}
        busy={checked && status === 'extracting'}
        busyLabel={
          isProfile
            ? (t('onboarding.documents.profilePhotoChecking') as string)
            : (t('documents.extracting') as string)
        }
      />

      {!ocr && !isProfile && value && !isUploading && (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
          <CheckCircle2 size={12} />
          {t('documents.uploaded')}
        </span>
      )}
      {isProfile && status === 'success' && !processingPhase && (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
          <CheckCircle2 size={12} />
          {t('onboarding.documents.profilePhotoAccepted')}
        </span>
      )}
      {isProfile && status === 'failed' && !processingPhase && (
        <div className="flex flex-col gap-1.5">
          <span className="inline-flex items-center gap-1 text-sm font-medium text-[#bf0035]">
            <AlertTriangle size={12} />
            {t('onboarding.documents.profilePhotoRejectedTitle')}
          </span>
          <ul className="flex list-disc flex-col gap-1 ps-5">
            {rejectionReasons.map((reason) => (
              <li key={reason} className="text-sm leading-relaxed text-[#bf0035]">
                {t(`onboarding.documents.profilePhotoReasons.${reason}`)}
              </li>
            ))}
          </ul>
        </div>
      )}
      {ocr && status === 'success' && !processingPhase && (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
          <CheckCircle2 size={12} />
          {t('documents.extracted')}
        </span>
      )}
      {ocr && status === 'failed' && !processingPhase && (
        <span className="inline-flex items-center gap-1 text-sm font-medium text-[#bf0035]">
          <AlertTriangle size={12} />
          {t('documents.extractionFailed')}
        </span>
      )}

      <RemoveDocumentModal
        open={removeOpen}
        documentLabel={label}
        dependents={buildDependents()}
        onClose={() => setRemoveOpen(false)}
        onConfirm={handleRemoveConfirm}
      />
    </section>
  );
};

export default CourierDocumentSlot;
